import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import {
    DraftSplit,
    makeKey,
    seedDrafts,
    suggestSplitsFromDocs,
} from './pageSplitPrefill';
import { GtFileDoc, GtPageSplit } from './types';

export interface SeedSource {
    splits: GtPageSplit[];
    /** Rows built from the pipeline's documents; only used when nothing is saved. */
    suggested: DraftSplit[];
}

const NO_SUGGESTIONS: DraftSplit[] = [];

interface UsePageSplitSeedArgs {
    /** Saved splits; `undefined` while loading. */
    existing: GtPageSplit[] | undefined;
    docs: GtFileDoc[] | undefined;
    docsPending: boolean;
    docsError: boolean;
    docsFailureCount: number;
    docTypes: ReadonlyArray<{ value: string }>;
    docTypesLoading: boolean;
    folderOptions: ReadonlyArray<{ value: string }>;
    foldersLoading: boolean;
    saveSucceeded: boolean;
}

/** Decides what the page-split grid starts from: saved splits, or rows suggested by the file's documents. */
export function usePageSplitSeed({
    existing,
    docs,
    docsPending,
    docsError,
    docsFailureCount,
    docTypes,
    docTypesLoading,
    folderOptions,
    foldersLoading,
    saveSucceeded,
}: UsePageSplitSeedArgs) {
    // A file with no saved splits starts from the documents the pipeline made
    // for it. Wait for everything the mapping needs so the rows are seeded once.
    const noSavedSplits = existing?.length === 0;
    // A failing docs read (e.g. a legacy doc type the backend can't serialise)
    // must not hold the editor on "Loading splits…" through React Query's
    // retries: treat the first failure as "no suggestions" and open blank.
    const docsFailed = docsError || docsFailureCount > 0;
    const awaitingPrefill =
        noSavedSplits &&
        ((docsPending && !docsFailed) || docTypesLoading || foldersLoading);
    const suggested = useMemo(
        () =>
            noSavedSplits
                ? suggestSplitsFromDocs(
                      docs ?? [],
                      new Set(docTypes.map((t) => t.value)),
                      folderOptions.map((f) => f.value),
                  ).map((row) => ({ ...row, key: makeKey(), remark: '', checked: false }))
                : NO_SUGGESTIONS,
        [noSavedSplits, docs, docTypes, folderOptions],
    );
    // Suggestions are frozen at the first ready render, so a docs refetch that
    // succeeds after the editor opened blank never re-seeds rows under the user.
    // This freeze, not the memo deps above, is what prevents re-seeding.
    // The seed source changes identity only with `existing` (not with the docs
    // or option queries), so deleting every suggested row can't bring them back.
    const readySuggestedRef = useRef<typeof suggested | null>(null);
    if (
        readySuggestedRef.current === null &&
        existing !== undefined &&
        !awaitingPrefill
    ) {
        readySuggestedRef.current = suggested;
    }
    const frozenSuggested = readySuggestedRef.current;
    const seedSource = useMemo(
        () =>
            existing === undefined || frozenSuggested === null
                ? undefined
                : { splits: existing, suggested: frozenSuggested },
        [existing, frozenSuggested],
    );

    // Prefill happens once, on the first load of a file with nothing saved —
    // not again after the operator saves (even zero rows) or clears the grid.
    const everSavedRef = useRef(false);
    const [prefilled, setPrefilled] = useState(false);
    useEffect(() => {
        if (!saveSucceeded) return;
        everSavedRef.current = true;
        setPrefilled(false);
    }, [saveSucceeded]);

    const shouldPrefill = useCallback(
        (source: SeedSource) =>
            source.splits.length === 0 &&
            source.suggested.length > 0 &&
            !everSavedRef.current,
        [],
    );
    const seed = useCallback(
        (source: SeedSource) => {
            if (source.splits.length > 0) everSavedRef.current = true;
            const prefill = shouldPrefill(source);
            setPrefilled(prefill);
            return prefill ? source.suggested : seedDrafts(source.splits);
        },
        [shouldPrefill],
    );

    return { seedSource, seed, shouldPrefill, awaitingPrefill, prefilled };
}
